import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram } from 'lucide-react';
import { Logo } from '../Logo';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../constants/appLinks';

const SOCIAL = [
  {
    href: 'https://www.facebook.com/profile.php?id=61562249256779',
    labelKey: 'footer.socialFacebook',
    Icon: Facebook,
  },
  {
    href: 'https://www.instagram.com/mindful.family_official/',
    labelKey: 'footer.socialInstagram',
    Icon: Instagram,
  },
] as const;

function FooterColumnTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="text-[11px] font-semibold uppercase tracking-[0.2em] text-content/45 mb-4 font-figtree"
    >
      {children}
    </h2>
  );
}

function FooterNavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block text-sm text-content/75 hover:text-accent-text transition-colors duration-200 py-0.5 font-figtree focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text/40 focus-visible:ring-offset-2 rounded-sm"
    >
      {children}
    </Link>
  );
}

function AppStoreButtons({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  const btnClass = compact
    ? 'w-[140px] h-11 sm:w-[152px] sm:h-12'
    : 'w-[152px] h-12';

  return (
    <div className="flex flex-col xs:flex-row gap-2.5">
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex ${btnClass} rounded-xl bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 items-center justify-center px-3 gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text/50 focus-visible:ring-offset-2`}
        aria-label={t('footer.appStoreAria')}
      >
        <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        <div className="text-left min-w-0">
          <div className="text-[10px] text-white/75 leading-tight truncate">{t('footer.appStoreLine1')}</div>
          <div className="text-xs font-semibold leading-tight truncate">{t('footer.appStoreLine2')}</div>
        </div>
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex ${btnClass} rounded-xl bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 items-center justify-center px-3 gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text/50 focus-visible:ring-offset-2`}
        aria-label={t('footer.googlePlayAria')}
      >
        <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
        </svg>
        <div className="text-left min-w-0">
          <div className="text-[10px] text-white/75 leading-tight truncate">{t('footer.googlePlayLine1')}</div>
          <div className="text-xs font-semibold leading-tight truncate">{t('footer.googlePlayLine2')}</div>
        </div>
      </a>
    </div>
  );
}

export function DesktopFooter() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="hidden lg:block relative mt-auto flex-shrink-0 border-t border-accent-text/10"
      role="contentinfo"
    >
      {/* Backgrounds clipped here so language dropdown can extend past footer bounds */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-green-50 via-white to-[#fafcf7]" />
        <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-accent-text/[0.06] blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-accent-text/[0.05] blur-2xl" />
      </div>

      <div className="relative z-10 max-w-page mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-8 xl:gap-x-12">
          {/* Brand + app */}
          <div className="col-span-12 xl:col-span-3 flex flex-col items-start">
            <Link
              to="/"
              className="group inline-flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text/40 focus-visible:ring-offset-2"
            >
              <Logo className="h-14 w-14 text-accent-text transition-transform duration-300 group-hover:scale-[1.03]" />
              <span
                className="text-xl font-gelica font-bold text-[#2F4F3E] transition-colors duration-200 group-hover:text-accent-text"
              >
                Mindful Family
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-content/65 max-w-sm font-figtree">
              {t('footer.tagline')}
            </p>
            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-content/45 font-figtree">
              {t('footer.getApp')}
            </p>
            <div className="mt-3">
              <AppStoreButtons compact />
            </div>
          </div>

          {/* Discover */}
          <nav className="col-span-6 md:col-span-4 xl:col-span-2" aria-labelledby="footer-discover-heading">
            <FooterColumnTitle id="footer-discover-heading">{t('nav.discover')}</FooterColumnTitle>
            <ul className="space-y-1 list-none p-0 m-0">
              <li>
                <FooterNavLink to="/events">{t('nav.events')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/communities">{t('nav.communities')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/practitioners">{t('nav.practitioners')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/venues">{t('nav.venues')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/jobs">{t('nav.jobs')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/conversations">{t('nav.conversations')}</FooterNavLink>
              </li>
            </ul>
          </nav>

          {/* Learn */}
          <nav className="col-span-6 md:col-span-4 xl:col-span-2" aria-labelledby="footer-learn-heading">
            <FooterColumnTitle id="footer-learn-heading">{t('nav.learn')}</FooterColumnTitle>
            <ul className="space-y-1 list-none p-0 m-0">
              <li>
                <FooterNavLink to="/courses">{t('nav.courses')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/recipes">{t('nav.recipes')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/articles">{t('nav.articles')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/blogs">{t('nav.blogs')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/live-stream">{t('nav.liveStream')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/features">{t('footer.features')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/tutorials">{t('footer.tutorials')}</FooterNavLink>
              </li>
            </ul>
          </nav>

          {/* Company */}
          <nav className="col-span-6 md:col-span-4 xl:col-span-2" aria-labelledby="footer-company-heading">
            <FooterColumnTitle id="footer-company-heading">{t('footer.company')}</FooterColumnTitle>
            <ul className="space-y-1 list-none p-0 m-0">
              <li>
                <FooterNavLink to="/about">{t('footer.aboutUs')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/pricing">{t('footer.pricing')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/ambassador">{t('footer.ambassador')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/corporate-wellness">{t('footer.corporateWellness')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/contact">{t('footer.contact')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/advertise">{t('footer.advertise')}</FooterNavLink>
              </li>
              <li>
                <FooterNavLink to="/host">{t('footer.hostVenues')}</FooterNavLink>
              </li>
            </ul>
          </nav>

          {/* Legal + social + language */}
          <div className="col-span-6 md:col-span-8 xl:col-span-3">
            <nav aria-labelledby="footer-legal-heading">
              <FooterColumnTitle id="footer-legal-heading">{t('footer.legal')}</FooterColumnTitle>
              <ul className="space-y-1 list-none p-0 m-0">
                <li>
                  <FooterNavLink to="/privacy-policy">{t('footer.privacyPolicy')}</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/terms">{t('footer.termsOfService')}</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/cookie-policy">{t('footer.cookiePolicy')}</FooterNavLink>
                </li>
              </ul>
            </nav>

            <div className="mt-8">
              <FooterColumnTitle>{t('footer.followUs')}</FooterColumnTitle>
              <div className="flex flex-wrap items-center gap-2">
                {SOCIAL.map(({ href, labelKey, Icon }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-content/10 bg-white/80 text-content/60 hover:text-accent-text hover:border-accent-text/30 hover:bg-accent-text/5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text/40 focus-visible:ring-offset-2"
                    aria-label={t(labelKey)}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-2">
              <FooterColumnTitle>{t('footer.language')}</FooterColumnTitle>
              <div className="mt-2">
                <LanguageSwitcher menuPlacement="below" menuAlign="start" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-8 border-t border-content/[0.08] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-content/55 font-figtree order-2 sm:order-1">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <p className="text-xs text-content/40 font-figtree order-1 sm:order-2 max-w-md sm:text-right leading-relaxed">
            {t('footer.madeWithCare')}
          </p>
        </div>
      </div>
    </footer>
  );
}
